import React, { useState } from 'react';

import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Select, MenuItem,
  FormControl, Box, Typography, Paper, TextField
} from '@mui/material';
import HelpIcon from './HelpIcon';

const PLANETS = [
  'Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Rahu'
];

const COLORS = [
  'Red', 'Green', 'Blue', 'Orange', 'Purple', 'Black', 'Gray', 'Brown', 'DeepPink', 'Teal', 'Navy', 'Olive', 'Maroon'
];

const COLOR_TO_HEX = {
  'Red': '#FF0000', 'Green': '#008000', 'Blue': '#0000FF', 'Orange': '#FFA500', 
  'Purple': '#800080', 'Black': '#000000', 'Gray': '#808080', 'Brown': '#A52A2A',
  'DeepPink': '#FF1493', 'Teal': '#008080', 'Navy': '#000080', 'Olive': '#808000', 'Maroon': '#800000',
  'Pink': '#FFC0CB', 'Cyan': '#00FFFF', 'Azure': '#F0FFFF', 'Yellow': '#FFFF00', 'White': '#FFFFFF'
};

const PLANET_DEFAULT_COLORS = {
  'Sun': 'Orange',
  'Moon': 'Gray',
  'Mercury': 'Green',
  'Venus': 'DeepPink',
  'Mars': 'Red',
  'Jupiter': 'Olive',
  'Saturn': 'Blue',
  'Uranus': 'Teal',
  'Neptune': 'Purple',
  'Pluto': 'Brown',
  'Rahu': 'Black'
};

const PlanetaryLinesDialog = ({ open, onClose, onRun }) => {
  const [mode, setMode] = useState('longitude'); // 'longitude' | 'declination'
  const [pricePer360, setPricePer360] = useState('200');
  const [color, setColor] = useState(COLOR_TO_HEX['DeepPink']); // Default for Venus (DeepPink)
  const [planet, setPlanet] = useState('Venus');
  const [thickness, setThickness] = useState('medium');
  const [lineType, setLineType] = useState('solid');
  // 'treat360' or 'drawRaw'
  const [longitudeOption, setLongitudeOption] = useState('treat360');

  // Octave coverage. Each octave is one full revolution of the planet, pricePer360 tall, stacked up
  // the price axis. Auto-fit is the default because the alternative is what users were doing by
  // hand: adding one line instance per N to reach SPY's $500.
  const [autoFitOctaves, setAutoFitOctaves] = useState(true);
  const [octaveStart, setOctaveStart] = useState('0');
  const [octaveEnd, setOctaveEnd] = useState('3');

  const isLongitudeMode = mode === 'longitude';

  const handlePlanetChange = (event) => {
    const newPlanet = event.target.value;
    setPlanet(newPlanet);
    if (PLANET_DEFAULT_COLORS[newPlanet]) {
      const colorName = PLANET_DEFAULT_COLORS[newPlanet];
      setColor(COLOR_TO_HEX[colorName] || colorName);
    }
  };

  const handleCustomColorChange = (event) => {
    setColor(event.target.value);
  };

  const handleRun = () => {
    onRun({
      planet,
      color,
      lineType,
      thickness,
      mode,
      pricePer360,
      drawRawLongitude: longitudeOption === 'drawRaw',
      autoFitOctaves,
      octaveStart,
      octaveEnd
    });
    onClose();
  };

  return (
    <Dialog open={open} fullWidth maxWidth="lg" PaperProps={{ style: { minWidth: 600, maxWidth: 700, borderRadius: 8 } }}>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Planetary Lines Study</span>
        <HelpIcon helpKey="planetaryLinesDialog" />
      </DialogTitle>
      <DialogContent sx={{ pt: 2, overflowX: 'visible', overflowY: 'visible', minWidth: 580 }}>
        
        {/* Rectangle 1: Mode Selection */}
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <input
              type="radio"
              checked={mode === 'longitude'}
              onChange={() => setMode('longitude')}
              style={{ marginRight: 4 }}
            />
            <Typography sx={{ minWidth: 90 }}>Longitude</Typography>

            <input
              type="radio"
              checked={mode === 'declination'}
              onChange={() => setMode('declination')}
              style={{ marginRight: 4 }}
            />
            <Typography sx={{ minWidth: 90 }}>Declination</Typography>
          </Box>
        </Paper>

        {/* Rectangle 2: Longitude Options */}
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, opacity: isLongitudeMode ? 1 : 0.5, pointerEvents: isLongitudeMode ? 'auto' : 'none' }}>
          <Box display="flex" alignItems="center" gap={2} mb={1} flexWrap="wrap">
            <input
              type="radio"
              checked={longitudeOption === 'treat360'}
              onChange={() => setLongitudeOption('treat360')}
              disabled={!isLongitudeMode}
              style={{ marginRight: 4 }}
            />
            <Typography sx={{ flexGrow: 1, color: isLongitudeMode ? undefined : '#888' }}>Treat 360 degrees equal to price</Typography>
            <TextField
              size="small"
              value={pricePer360}
              onChange={e => setPricePer360(e.target.value)}
              sx={{ width: 80 }}
              disabled={!isLongitudeMode || longitudeOption !== 'treat360'}
            />
          </Box>

          <Box display="flex" alignItems="center" gap={2} mb={1} flexWrap="wrap">
            <input
              type="radio"
              checked={longitudeOption === 'drawRaw'}
              onChange={() => setLongitudeOption('drawRaw')}
              disabled={!isLongitudeMode}
              style={{ marginRight: 4 }}
            />
            <Typography sx={{ color: isLongitudeMode ? undefined : '#888' }}>Draw Raw Degrees</Typography>
          </Box>

          {/* Octave coverage. One octave = one revolution = pricePer360 of vertical space. */}
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <input
              type="checkbox"
              id="planetary-lines-autofit"
              checked={autoFitOctaves}
              onChange={e => setAutoFitOctaves(e.target.checked)}
              disabled={!isLongitudeMode}
              style={{ marginRight: 4 }}
            />
            <Typography component="label" htmlFor="planetary-lines-autofit"
              sx={{ color: isLongitudeMode ? undefined : '#888' }}>
              Auto-fit to price axis
            </Typography>
            <Typography sx={{ ml: 2, color: (!isLongitudeMode || autoFitOctaves) ? '#888' : undefined }}>
              Octaves N =
            </Typography>
            <TextField
              size="small" type="number" value={octaveStart}
              onChange={e => setOctaveStart(e.target.value)}
              sx={{ width: 70 }}
              inputProps={{ 'aria-label': 'Octave start' }}
              disabled={!isLongitudeMode || autoFitOctaves}
            />
            <Typography sx={{ color: (!isLongitudeMode || autoFitOctaves) ? '#888' : undefined }}>to</Typography>
            <TextField
              size="small" type="number" value={octaveEnd}
              onChange={e => setOctaveEnd(e.target.value)}
              sx={{ width: 70 }}
              inputProps={{ 'aria-label': 'Octave end' }}
              disabled={!isLongitudeMode || autoFitOctaves}
            />
          </Box>
        </Paper>

        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mt={2} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography>Planet</Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select value={planet} onChange={handlePlanetChange}>
                {PLANETS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography>Color</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select 
                  value={Object.keys(COLOR_TO_HEX).find(key => COLOR_TO_HEX[key] === color) || 'Custom'} 
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val !== 'Custom' && COLOR_TO_HEX[val]) {
                      setColor(COLOR_TO_HEX[val]);
                    }
                  }}
                  renderValue={selected => {
                    if (selected === 'Custom') return <Box display="flex" alignItems="center">Custom</Box>;
                    return (
                      <Box display="flex" alignItems="center">
                        <span style={{ display: 'inline-block', width: 18, height: 18, borderRadius: '50%', backgroundColor: COLOR_TO_HEX[selected], marginRight: 8, border: '1px solid #bbb' }} />
                        {selected}
                      </Box>
                    );
                  }}
                >
                  {COLORS.map(c => (
                    <MenuItem key={c} value={c}>
                      <Box display="flex" alignItems="center">
                        <span style={{ display: 'inline-block', width: 18, height: 18, borderRadius: '50%', backgroundColor: COLOR_TO_HEX[c], marginRight: 8, border: '1px solid #bbb' }} />
                        {c}
                      </Box>
                    </MenuItem>
                  ))}
                  <MenuItem value="Custom" style={{ display: 'none' }}>Custom</MenuItem>
                </Select>
              </FormControl>
              <input
                type="color"
                value={color}
                onChange={handleCustomColorChange}
                style={{ width: 40, height: 40, padding: 0, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', backgroundColor: 'transparent' }}
                title="Choose custom color"
              />
            </Box>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mt={2} flexWrap="wrap">
            <Box display="flex" alignItems="center" gap={1.5}>
              <Typography>Thickness</Typography>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select value={thickness} onChange={(e) => setThickness(e.target.value)}>
                  <MenuItem value="thin">Thin</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="thick">Thick</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box display="flex" alignItems="center" gap={1.5}>
              <Typography>Type</Typography>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select value={lineType} onChange={(e) => setLineType(e.target.value)}>
                  <MenuItem value="solid">Solid</MenuItem>
                  <MenuItem value="dashed">Dashed</MenuItem>
                  <MenuItem value="dotted">Dotted</MenuItem>
                </Select>
              </FormControl>
            </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px', justifyContent: 'space-between', gap: 2 }}>
        <Button onClick={onClose} variant="contained" color="inherit">CLOSE</Button>
        <Button variant="contained" sx={{ backgroundColor: '#8e44ad', '&:hover': { backgroundColor: '#732d91' } }} onClick={handleRun}>RUN</Button>
      </DialogActions>
    </Dialog>
  );
};

PlanetaryLinesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRun: PropTypes.func.isRequired,
};

export default PlanetaryLinesDialog;
